import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PHASES_OF_PLAY, PHASE_OF_PLAY_META } from '@4cm/core/phaseOfPlay';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { BrainOrb, Button, Card, GlassPill, Skeleton } from '../components/primitives';
import { supabase } from '../lib/supabase';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamSessions, type SessionsRow } from '../lib/queries/useTeamSessions';
import { useTeamGames } from '../lib/queries/useTeamGames';
import { AddEventModal } from '../components/AddEventModal';
import { useAuth } from '../contexts/AuthContext';
import { useCalibration } from '../contexts/CalibrationContext';
import './page-shell.css';
import './Calendar.css';

type SessionRow = SessionsRow;

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function startOfWeek(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const day = out.getDay() || 7;
  out.setDate(out.getDate() - (day - 1));
  return out;
}

/** Local YYYY-MM-DD — matches a Date against a game's DATE column. */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function topPhase(coverage: Record<string, number> | null): PhaseOfPlay | null {
  if (!coverage) return null;
  let best: PhaseOfPlay | null = null;
  let bestValue = 0;
  for (const phase of PHASES_OF_PLAY) {
    const value = Number(coverage[phase] ?? 0);
    if (value > bestValue) { best = phase; bestValue = value; }
  }
  return best;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
}

export function Calendar() {
  const { team, loading: teamLoading } = useActiveTeam();
  const { level } = useCalibration();
  const navigate = useNavigate();
  const [weekOffset, setWeekOffset] = useState(0);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetIso, setDropTargetIso] = useState<string | null>(null);
  const [rescheduleMessage, setRescheduleMessage] = useState<string | null>(null);
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState<string | null>(null);

  const { weekStart, weekEnd } = useMemo(() => {
    const start = startOfWeek(new Date());
    start.setDate(start.getDate() + weekOffset * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { weekStart: start, weekEnd: end };
  }, [weekOffset]);

  const { rows: hookSessions, state, errorMessage } = useTeamSessions(team?.id, teamLoading, {
    since: weekStart,
    until: weekEnd,
    order: 'asc',
  });

  // Mirror the hook's rows into local state so the optimistic-reschedule
  // mutation can move a session between days without waiting for a refetch.
  // Re-syncs whenever the upstream rows change (e.g. week navigation).
  const [sessions, setSessions] = useState<SessionRow[]>(hookSessions);
  useEffect(() => { setSessions(hookSessions); }, [hookSessions]);

  const { rows: games, refresh: refreshGames } = useTeamGames(team?.id, teamLoading);

  /**
   * Move a session to the dropped day. Preserves time-of-day from the
   * session's original date. Optimistically updates local state then
   * writes to the DB; on error, surfaces the message and reverts.
   */
  const reschedule = async (sessionId: string, newDay: Date) => {
    const original = sessions.find((s) => s.id === sessionId);
    if (!original) return;
    const oldDate = new Date(original.date);
    const merged = new Date(newDay);
    merged.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
    if (merged.toDateString() === oldDate.toDateString()) return;

    const newIso = merged.toISOString();
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, date: newIso } : s)));
    setRescheduleMessage(null);

    const revert = () =>
      setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, date: original.date } : s)));

    try {
      const { error } = await supabase
        .from('sessions')
        .update({ date: newIso })
        .eq('id', sessionId);
      if (error) {
        revert();
        setRescheduleMessage(error.message);
        return;
      }
      setRescheduleMessage(`Moved to ${merged.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}.`);
    } catch (err) {
      // Offline / placeholder Supabase — keep the UI honest and tell the
      // coach the move didn't persist instead of leaving it visually moved.
      revert();
      const reason = err instanceof Error ? err.message : 'reschedule failed';
      setRescheduleMessage(`Could not move session — ${reason}`);
    }
  };

  const todayStr = new Date().toDateString();
  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      return {
        date,
        label: WEEKDAY_LABELS[i],
        dayOfMonth: date.getDate(),
        isToday: date.toDateString() === todayStr,
        sessions: sessions.filter((s) => new Date(s.date).toDateString() === date.toDateString()),
        games: games.filter((g) => g.date === ymd(date)),
      };
    });
  }, [weekStart, sessions, games, todayStr]);

  const weekLabel = `${weekStart.toLocaleDateString([], { month: 'short', day: 'numeric' })} — ${new Date(weekEnd.getTime() - 1).toLocaleDateString([], { month: 'short', day: 'numeric' })}`;

  // Week phase mix — aggregate phase_coverage across all sessions in the visible week.
  // Surfaced for academy only (workbench periodisation insight per
  // 03_wireframes.md §4 academy variant). Novice variant suppresses to keep
  // the calendar a simple "what's happening this week" surface.
  const phaseTotals = useMemo(() => {
    const totals: Record<PhaseOfPlay, number> = {
      attacking: 0, defending: 0, transitions: 0, individual: 0,
    };
    for (const s of sessions) {
      if (!s.phase_coverage) continue;
      for (const phase of PHASES_OF_PLAY) {
        totals[phase] += Number(s.phase_coverage[phase] ?? 0);
      }
    }
    return totals;
  }, [sessions]);
  const phaseTotalSum = PHASES_OF_PLAY.reduce((sum, p) => sum + phaseTotals[p], 0);
  const showPhaseMix = level === 'academy' && phaseTotalSum > 0;

  return (
    <main className="main page calendar-page">
      <header className="page-head calendar-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 2 · planning</span>
          <h1 className="page-title">Calendar</h1>
          <p className="page-sub">{weekLabel}</p>
        </div>
        <div className="calendar-head__plan">
          <Button variant="primary" size="touchline" onClick={() => setAddOpen(ymd(new Date()))}>+ Event</Button>
          <Button variant="glass" size="touchline" onClick={() => navigate('/season')}>Plan season</Button>
          <Button
            variant="primary"
            size="touchline"
            onClick={() => {
              const m = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
              navigate(`/season?week=${m}`);
            }}
          >
            Plan this week
          </Button>
        </div>
        <div className="calendar-nav">
          <button type="button" className="calendar-nav__btn" onClick={() => setWeekOffset((o) => o - 1)} aria-label="Previous week">‹</button>
          <button type="button" className="calendar-nav__btn" onClick={() => setWeekOffset(0)} aria-label="This week">today</button>
          <button type="button" className="calendar-nav__btn" onClick={() => setWeekOffset((o) => o + 1)} aria-label="Next week">›</button>
        </div>
      </header>

      {rescheduleMessage && <p className="calendar-toast">{rescheduleMessage}</p>}

      {showPhaseMix && (
        <section className="calendar-phase-mix" aria-label="Week phase coverage">
          <div className="page-section-label">Week phase mix</div>
          <div className="calendar-phase-mix__bar" role="img" aria-label="Phase coverage proportions across the visible week">
            {PHASES_OF_PLAY.map((phase) => {
              const value = phaseTotals[phase];
              if (value <= 0) return null;
              const pct = Math.round((value / phaseTotalSum) * 100);
              return (
                <span
                  key={phase}
                  className="calendar-phase-mix__seg"
                  data-phase={phase}
                  style={{ flex: pct, background: PHASE_OF_PLAY_META[phase].colorHex }}
                  title={`${PHASE_OF_PLAY_META[phase].label} ${pct}%`}
                >
                  <span className="calendar-phase-mix__pct">{pct}%</span>
                </span>
              );
            })}
          </div>
          <div className="calendar-phase-mix__legend">
            {PHASES_OF_PLAY.map((phase) => {
              const value = phaseTotals[phase];
              if (value <= 0) return null;
              return (
                <span key={phase} className="calendar-phase-mix__legend-item">
                  <span aria-hidden="true">{PHASE_OF_PLAY_META[phase].glyph}</span>
                  {PHASE_OF_PLAY_META[phase].label}
                </span>
              );
            })}
          </div>
        </section>
      )}

      {state === 'loading' && (
        <div className="calendar-grid" aria-label="Loading calendar">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="calendar-col">
              <Skeleton variant="text" width="60%" height={12} />
              <div style={{ height: 8 }} />
              <Skeleton variant="card" />
            </div>
          ))}
        </div>
      )}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn't load the week.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}

      {state !== 'loading' && state !== 'error' && (
        <div className="calendar-grid">
          {days.map((day) => {
            const dayIso = day.date.toISOString();
            return (
            <div
              key={dayIso}
              className="calendar-col"
              data-today={day.isToday || undefined}
              data-drop-active={dropTargetIso === dayIso || undefined}
              onDragOver={(e) => {
                if (!draggingId) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dropTargetIso !== dayIso) setDropTargetIso(dayIso);
              }}
              onDragLeave={() => {
                if (dropTargetIso === dayIso) setDropTargetIso(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/session-id') || draggingId;
                setDropTargetIso(null);
                setDraggingId(null);
                if (id) void reschedule(id, day.date);
              }}
            >
              <div className="calendar-col__head">
                <span className="calendar-col__weekday">{day.label}</span>
                <span className="calendar-col__day">{day.dayOfMonth}</span>
              </div>
              <div className="calendar-col__body">
                {day.sessions.length === 0 && day.games.length === 0 && (
                  <div className="calendar-col__empty">—</div>
                )}
                {day.sessions.map((s) => {
                  const phase = topPhase(s.phase_coverage);
                  return (
                    <Link
                      key={s.id}
                      to={`/sessions/${s.id}`}
                      className="calendar-event"
                      draggable
                      data-dragging={draggingId === s.id || undefined}
                      onDragStart={(e) => {
                        setDraggingId(s.id);
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/session-id', s.id);
                      }}
                      onDragEnd={() => { setDraggingId(null); setDropTargetIso(null); }}
                    >
                      <Card variant="glass">
                        <div className="calendar-event__time">{formatTime(new Date(s.date))}</div>
                        <div className="calendar-event__title">{s.title ?? 'Session'}</div>
                        {(s.duration_mins || phase) && (
                          <div className="calendar-event__meta">
                            {phase && (
                              <GlassPill mono>
                                <span aria-hidden="true">{PHASE_OF_PLAY_META[phase].glyph}</span>
                                {PHASE_OF_PLAY_META[phase].label}
                              </GlassPill>
                            )}
                            {s.duration_mins && <span className="calendar-event__duration">{s.duration_mins}m</span>}
                          </div>
                        )}
                      </Card>
                    </Link>
                  );
                })}
                {day.games.map((g) => (
                  <Link key={g.id} to={`/games/${g.id}`} className="calendar-event calendar-event--game">
                    <Card variant="glass">
                      <div className="calendar-event__time">
                        {g.kickoff_time ? formatTime(new Date(g.kickoff_time)) : (g.competition ?? 'Match')}
                      </div>
                      <div className="calendar-event__title">vs {g.opponent}</div>
                      <div className="calendar-event__meta">
                        {g.competition && <GlassPill mono>{g.competition}</GlassPill>}
                        <span className="calendar-event__duration">{g.home_away}</span>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {addOpen && team && (
        <AddEventModal
          teamId={team.id}
          coachId={user?.id ?? null}
          defaultDate={addOpen}
          onClose={() => setAddOpen(null)}
          onCreated={(r) => {
            setAddOpen(null);
            if (r.kind === 'session') navigate(`/sessions/${r.id}`);
            else refreshGames();
          }}
        />
      )}
    </main>
  );
}
